/**
 * Pureformance CRO — cart Subscribe & Save switch (28 Sep 2026)
 *
 * Two jobs:
 *
 *  1. In-cart switch. Every one-time line that has a subscription plan renders a
 *     `[data-pf-subswitch]` card (see snippets/cart-products.liquid). Clicking its
 *     button swaps THAT line to the plan in place via POST /cart/change.js, asking
 *     Shopify to also return the re-rendered cart sections, which are then morphed
 *     into the page/drawer with the theme's own section renderer — the same path the
 *     quantity selector uses, so totals, the delivery line and the drawer badge all
 *     update without a reload.
 *
 *  2. Deep link from email/SMS:  /cart?pf_variant=<variant id>&pf_plan=<plan id|auto>[&pf_handle=<product handle>]
 *     Lands the shopper on a cart with that variant ON the subscription plan,
 *     whatever device they open it on:
 *       - variant already in the cart as one-time  → change that line to the plan
 *       - variant already in the cart on the plan  → nothing to do
 *       - variant not in the cart (different device / cleared cart) → add it on the plan
 *     `pf_plan=auto` resolves the plan from the product's own selling-plan
 *     allocations (needs pf_handle when the cart is empty). The URL is then
 *     cleaned to /cart?pf_switched=1 and a confirmation notice is shown.
 *
 * Fallbacks: any failure in the morph path reloads the page (the cart mutation
 * itself has already succeeded at that point), and any failure in the deep-link
 * path simply lands on /cart untouched.
 */
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

const SWITCH_SELECTOR = '[data-pf-subswitch]';
const BUTTON_SELECTOR = '[data-pf-subswitch-button]';
const NOTICE_SELECTOR = '[data-pf-cart-notice]';

/* ------------------------------------------------------------------ helpers */

function routes() {
  const t = window.Theme && window.Theme.routes ? window.Theme.routes : {};
  return {
    change: t.cart_change_url || '/cart/change',
    add: t.cart_add_url || '/cart/add.js',
    cart: t.cart_url || '/cart',
  };
}

function cartSections() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let json = {};
  try {
    json = JSON.parse(text);
  } catch (e) {
    json = {};
  }
  if (!res.ok || json.errors || json.status >= 400) {
    const message = json.description || json.message || json.errors || `Cart request failed (${res.status})`;
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return json;
}

async function getCart() {
  const res = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Failed to read cart (${res.status})`);
  return res.json();
}

async function morphCart(sections) {
  const ids = cartSections();
  if (!ids.length) return false;
  let morphed = false;
  for (const id of ids) {
    const host = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
    const isDrawer = host instanceof HTMLElement && host.dataset.drawer !== undefined;
    const html = sections && sections[id];
    sectionRenderer.abortRender(id);
    if (html) {
      await morphSection(id, html, { mode: isDrawer ? 'hydration' : 'full' });
      morphed = true;
    } else {
      await sectionRenderer.renderSection(id, { cache: false, mode: isDrawer ? 'hydration' : 'full' });
      morphed = true;
    }
  }
  // Re-sync the cart quantity selector button states after a morph (mirrors the theme).
  for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
    if (typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
  }
  return morphed;
}

function showNotice(html) {
  const notice = document.querySelector(NOTICE_SELECTOR);
  if (!(notice instanceof HTMLElement)) return;
  notice.innerHTML = html;
  notice.hidden = false;
}

function noticeHtml(saving) {
  const savingText = saving ? ` You&rsquo;re saving <strong>${saving}</strong> on this order.` : '';
  return `<span aria-hidden="true">&#10003;</span><span><strong>Subscribe &amp; Save applied.</strong>${savingText} Pause, skip or cancel anytime from your account.</span>`;
}

/* ------------------------------------------------------- in-cart switch (1) */

async function switchLine(card) {
  const button = card.querySelector(BUTTON_SELECTOR);
  const line = Number(card.dataset.line);
  const quantity = Number(card.dataset.quantity) || 1;
  const plan = Number(card.dataset.plan);
  const saving = card.dataset.saving || '';
  if (!line || !plan) return;

  if (button instanceof HTMLButtonElement) button.disabled = true;
  card.classList.remove('pf-subswitch--error');
  const oldError = card.querySelector('.pf-subswitch__error');
  if (oldError) oldError.remove();

  const ids = cartSections();
  let json;
  try {
    json = await postJson(routes().change, {
      line,
      quantity,
      selling_plan: plan,
      sections: ids.join(','),
      sections_url: window.location.pathname,
    });
  } catch (error) {
    // The mutation itself failed — tell the shopper and leave the line as it was.
    if (button instanceof HTMLButtonElement) button.disabled = false;
    card.classList.add('pf-subswitch--error');
    const p = document.createElement('p');
    p.className = 'pf-subswitch__error';
    p.textContent = "Sorry, that didn't work — please try again or choose Subscribe & Save on the product page.";
    card.appendChild(p);
    return;
  }

  try {
    await morphCart(json.sections);
    if (document.querySelector(NOTICE_SELECTOR)) showNotice(noticeHtml(saving));
  } catch (error) {
    // The cart is already switched server-side; a reload shows the truth.
    window.location.reload();
  }
}

/* ------------------------------------------- "Complete your stack" add (3) */

async function addFromUpsell(button) {
  const variantId = Number(button.dataset.variant);
  const plan = Number(button.dataset.plan) || null;
  if (!variantId) return;
  const card = button.closest('[data-pf-upsell]');
  const buttons = card ? card.querySelectorAll('[data-pf-add]') : [button];
  buttons.forEach((b) => { b.disabled = true; });

  const item = { id: variantId, quantity: 1 };
  if (plan) item.selling_plan = plan;
  const ids = cartSections();

  // Mirror the theme's own add-to-cart: announce the add with a promise, and let
  // cart-items-component / cart-icon morph the sections and update the bubble.
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const json = await postJson(routes().add, { items: [item], sections: ids.join(','), sections_url: window.location.pathname });
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'pf-cart',
        itemCount: cart.item_count,
        sections: json.sections,
        didError: false,
      },
    });
    if (card instanceof HTMLElement) {
      card.classList.add('pf-upsell--removing');
      setTimeout(() => card.remove(), 220);
    }
  } catch (error) {
    deferred.reject(error);
    buttons.forEach((b) => { b.disabled = false; });
    console.warn('[pf-cart] add failed:', error);
  }
}

/* ------------------------------------------ "switch everything" link (4) */

let switchingAll = false;

async function switchAll(link) {
  if (switchingAll) return;
  switchingAll = true;
  link.setAttribute('aria-busy', 'true');
  try {
    // Each switch re-renders the cart, so re-query for the next remaining card.
    for (let i = 0; i < 10; i += 1) {
      const card = document.querySelector(SWITCH_SELECTOR);
      if (!(card instanceof HTMLElement)) break;
      await switchLine(card);
      if (card.classList.contains('pf-subswitch--error')) break;
    }
  } finally {
    switchingAll = false;
    const current = document.querySelector('[data-pf-switch-all]');
    if (current) current.removeAttribute('aria-busy');
  }
}

/* --------------------------------------------- sticky checkout bar (5) */

function setupStickyBar() {
  if (!document.querySelector('[data-pf-sticky]')) return;
  const phone = window.matchMedia('(max-width: 749px)');
  let ticking = false;
  const update = () => {
    ticking = false;
    const checkout = document.getElementById('checkout');
    const bar = document.querySelector('[data-pf-sticky]');
    if (!checkout || !bar || !phone.matches) {
      document.documentElement.classList.remove('pf-sticky-show');
      return;
    }
    const r = checkout.getBoundingClientRect();
    const onScreen = r.bottom > 0 && r.top < window.innerHeight;
    document.documentElement.classList.toggle('pf-sticky-show', !onScreen);
  };
  const schedule = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  if (typeof phone.addEventListener === 'function') phone.addEventListener('change', schedule);
  document.addEventListener('cart:update', schedule);
  update();
  // Re-check after the section renderer morphs the cart (totals/button move).
  const host = document.querySelector('cart-items-component');
  if (host) new MutationObserver(schedule).observe(host, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupStickyBar);
} else {
  setupStickyBar();
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const addButton = target.closest('[data-pf-add]');
  if (addButton instanceof HTMLButtonElement) {
    event.preventDefault();
    addFromUpsell(addButton);
    return;
  }

  const switchAllLink = target.closest('[data-pf-switch-all]');
  if (switchAllLink instanceof HTMLElement) {
    event.preventDefault();
    switchAll(switchAllLink);
    return;
  }

  const button = target.closest(BUTTON_SELECTOR);
  if (!button) return;
  const card = button.closest(SWITCH_SELECTOR);
  if (!(card instanceof HTMLElement)) return;
  event.preventDefault();
  switchLine(card);
});

/* --------------------------------------------------------- deep link (2) */

async function resolvePlan(handle, variantId) {
  if (!handle) return null;
  const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) return null;
  const product = await res.json();
  const variant = (product.variants || []).find((v) => Number(v.id) === Number(variantId));
  const alloc = variant && variant.selling_plan_allocations && variant.selling_plan_allocations[0];
  return alloc ? Number(alloc.selling_plan_id) : null;
}

async function handleDeepLink() {
  if (!window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;
  const params = new URLSearchParams(window.location.search);

  if (params.get('pf_switched') === '1') {
    showNotice(noticeHtml(''));
    return;
  }

  const variantId = Number(params.get('pf_variant'));
  if (!variantId) return;
  let plan = params.get('pf_plan') || 'auto';
  const handleParam = params.get('pf_handle') || '';

  const cleanUrl = (switched) => {
    const url = new URL(window.location.href);
    ['pf_variant', 'pf_plan', 'pf_handle'].forEach((k) => url.searchParams.delete(k));
    if (switched) url.searchParams.set('pf_switched', '1');
    return url.pathname + (url.search || '');
  };

  try {
    const cart = await getCart();
    const items = cart.items || [];
    // Prefer a one-time line for this variant; fall back to any line for it.
    const lineIndex = items.findIndex((i) => Number(i.variant_id) === variantId && !i.selling_plan_allocation);
    const anyIndex = items.findIndex((i) => Number(i.variant_id) === variantId);

    if (plan === 'auto') {
      const handle = handleParam || (anyIndex >= 0 ? items[anyIndex].handle : '');
      const resolved = await resolvePlan(handle, variantId);
      if (!resolved) {
        window.location.replace(cleanUrl(false));
        return;
      }
      plan = resolved;
    }
    plan = Number(plan);

    if (lineIndex >= 0) {
      const item = items[lineIndex];
      await postJson(routes().change, { line: lineIndex + 1, quantity: item.quantity, selling_plan: plan });
      window.location.replace(cleanUrl(true));
      return;
    }

    if (anyIndex >= 0) {
      // Already on a plan — nothing to change.
      window.location.replace(cleanUrl(true));
      return;
    }

    await postJson(routes().add, { items: [{ id: variantId, quantity: 1, selling_plan: plan }] });
    window.location.replace(cleanUrl(true));
  } catch (error) {
    console.warn('[pf-cart] deep link failed:', error);
    window.location.replace(cleanUrl(false));
  }
}

handleDeepLink();
